import {
  Box,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PreviewModeType } from '../../../models/BlockProps';
import { BlockDataPerPage } from '../../../contexts/PagesContext';
import DataManager from '../../../managers/DataManager';
import BlockType from '../../../models/BlockType';
import BlockSlot from './BlockSlot';
import BlockHeader from '../Headers/BlockHeader/BlockHeader';
import BlockHeaderWithLogo from '../Headers/BlockHeaderWithLogo/BlockHeaderWithLogo';
import BlockHeaderLogoMenu from '../Headers/BlockHeaderLogoMenu/BlockHeaderLogoMenu';
import BlockTitle from '../BlockTitle/BlockTitle';
import BlockImage from '../BlockImage/BlockImage';
import BlockTextWithBackground from '../BlockTextWithBackground/BlockTextWithBackground';
import BlockImageAndText from '../BlockImageAndText/BlockImageAndText';
import BlockMenuList3 from '../BlockMenuList/BlockMenuList';
import BlockMap from '../BlockMap/BlockMap';
import BlockText from '../BlockText/BlockText';
import BlockiFrame from '../BlockiFrame/BlockiFrame';
import BlockTextList from '../BlockTextList/BlockTextList';
import BlockButton from '../BlockButton/BlockButton';
import BlockHtml from '../BlockHtml/BlockHtml';
import BlockCard from '../BlockCard/BlockCard';
import BlockCardCentered from '../BlockCardCentered/BlockCardCentered';
import BlockTitleTextButton from '../BlockTitleTextButton/BlockTitleTextButton';
import BlockButtonList from '../BlockButtonList/BlockButtonList';
import BlockCarouselWithPureJs from '../BlockCarousel/BlockCarouselWithPureJs';
import BlockImageList from '../BlockImageList/BlockImageList';
import BlockTable from '../BlockTable/BlockTable';
import BlockCenteredImage from '../BlockCenteredImage/BlockCenteredImage';
import BlockFullBleedImage from '../BlockFullBleedImage/BlockFullBleedImage';
import BlockLinedTitle from '../BlockLinedTitle/BlockLinedTitle';
import BlockSNSOldPreview from '../BlockSNSOld/BlockSNSOldPreview';
import BlockSNSPreview from '../BlockSNS/BlockSNSPreview';
import BlockAnimationBlock from '../BlockAnimationBlocks/BlockAnimationBlock';
import BlockFooter from '../BlockFooter/BlockFooter';
import api from '../../../services/api';
import BlockContact from '../BlockContact/BlockContact';
import AdContainer from '../../Ads/AdContainer';
import BlockVideo from '../BlockVideo/BlockVideo';
import BlockAnimationWithImageDescription from '../BlockAnimationWithImageDescription/BlockAnimationWithImageDescription';
import BlockMenuListWithColumns from '../BlockMenuListWithColumns/BlockMenuListWithColumns';
import BlockMenuListWithEqualSpace from '../BlockMenuListWithEqualSpace/BlockMenuListWithEqualSpace';
import BlockHtmlSafe from '../BlockHtmlSafe/BlockHtmlSafe';
import BlockiFrameSafe from '../BlockiFrameSafe/BlockiFrameSafe';
import BlockiFrameServices from '../BlockiFrameServices/BlockiFrameServices';
import BlockMenuListFixedMax2 from '../BlockMenuListFixedMax2/BlockMenuListFixedMax2';
import BlockMenuListFixedMax3 from '../BlockMenuListFixedMax3/BlockMenuListFixedMax3';
import BlockMenuListFixedMobile2 from '../BlockMenuListFixedMobile2/BlockMenuListFixedMobile2';
import BlockMenuListEqualMobile2 from '../BlockMenuListEqualMobile2/BlockMenuListEqualMobile2';
import BlockMenuListEqualMax2 from '../BlockMenuListEqualMax2/BlockMenuListEqualMax2';
import BlockMenuListEqualMax3 from '../BlockMenuListEqualMax3/BlockMenuListEqualMax3';

interface Props {
  pageId: string
}

function BlocksContainerExport(props: Props) {
  // variables
  const { pageId } = props;

  const [previewMode] = useState<PreviewModeType>('pc');
  const [page, setPage] = useState<BlockDataPerPage>();
  const [isLoading, setIsLoading] = useState(false);

  const { page_setting = {} } = page ?? {};
  const { animation } = page_setting;
  const { background_color } = page_setting;
  const { main_color } = page_setting;
  const { text_color } = page_setting;
  const { text_font } = page_setting;

  // data manager
  const dataManager = useRef<DataManager>(new DataManager());
  const blockItems = dataManager.current.getCurrent();

  // display functions
  const boxWidth = useMemo(() => {
    if (previewMode === 'pc') {
      return '100%';
    }

    return 375;
  }, [previewMode]);

  // effects
  useEffect(() => {
    setIsLoading(true);

    async function awaitGetPage() {
      await api.get(`v1/pages/${pageId}`)
        .then((res) => {
          const { data } = res;
          const blocksData = JSON.parse(data.params);

          setPage(data);

          dataManager.current.loadBlocks(blocksData);
          // forceUpdate();
        }).finally(() => {
          setIsLoading(false);
        });
    }

    awaitGetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {
        isLoading && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '30vh',
            }}
          >
            <CircularProgress size={20} />
          </div>
        )
      }
      {/* PDEV3-34: keep published/preview content in its original language (no auto-translation) */}
      <Grid container id="blocksContainer" translate="no" className="notranslate">
        <Grid style={{ height: '100%', width: '100%' }} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box width={boxWidth}>
            <Grid container style={{ height: '100%', width: '100%' }} justifyContent="center">
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#fff',
                  boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Stack
                  style={{ backgroundColor: background_color, color: text_color, fontFamily: text_font }}
                >
                  {blockItems.map((item) => {
                    switch (item.type) {
                      case BlockType.HEADER:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={item.data?.isFree}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                          // if case is free, background color is white
                          >
                            <BlockHeader
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              backgroundColor={background_color ?? '#ffff'}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.HEADER_FREE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={item.data?.isFree}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockHeader
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              backgroundColor={background_color ?? '#ffff'}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.HEADER_WITH_LOGO:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                          >
                            <BlockHeaderWithLogo
                              mainColor={main_color}
                              backgroundColor={background_color ?? '#ffff'}
                              fontFamily={text_font}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.HEADER_WITH_LOGO_FREE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={item.data?.isFree}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockHeaderWithLogo
                              mainColor={main_color}
                              backgroundColor={background_color ?? '#ffff'}
                              fontFamily={text_font}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.HEADER_LOGO_MENU:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                          >
                            <BlockHeaderLogoMenu
                              backgroundColor={background_color ?? '#ffff'}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.HEADER_LOGO_MENU_FREE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={item.data?.isFree}
                            isHeader={!item.data?.isFree}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockHeaderLogoMenu
                              backgroundColor={background_color ?? '#ffff'}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.TITLE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTitle
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.IMAGE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockImage
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              blockData={blockItems}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.TXT_BACKGROUND:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTextWithBackground
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.TXT_BACKGROUND_TALL:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTextWithBackground
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.IMAGE_TEXT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            handleBlockAddClicked={() => { }}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockImageAndText
                              blockData={blockItems}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.MENU_LIST_3:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuList3
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.MAP:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMap data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.I_FRAME:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockiFrame data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.I_FRAME_SAFE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockiFrameSafe data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.TEXT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockText onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.TEXT_LIST:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTextList onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.BUTTON:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockButton mainColor={main_color} onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} blockData={blockItems} />
                          </BlockSlot>
                        );
                      case BlockType.HTML:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockHtml data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.HTML_SAFE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockHtmlSafe data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.CARD_LEFT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCard
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CARD_RIGHT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCard
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CARD_LEFT_CENTERED:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCardCentered
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CARD_RIGHT_CENTERED:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCardCentered
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.TITLE_TXT_BUTTON:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTitleTextButton mainColor={main_color} onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} blockData={blockItems} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.TITLE_TXT_BUTTON_TALL:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTitleTextButton onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} blockData={blockItems} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.BUTTON_LIST:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockButtonList mainColor={main_color} onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} blockData={blockItems} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.CAROUSEL:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCarouselWithPureJs
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CAROUSEL_TALL:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCarouselWithPureJs
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CAROUSEL_FIXED:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCarouselWithPureJs
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CAROUSEL_FIXED_TALL:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCarouselWithPureJs
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.IMAGE_LIST:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockImageList onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} blockData={blockItems} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.TABLE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTable onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.CENTERED_IMAGE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCenteredImage
                              showImageModal={false}
                              onCloseImageModal={() => { }}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.TABLE_BOTTOM_BORDER:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTable onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.TABLE_NO_BORDER:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockTable onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.FULL_BLEED_IMAGE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isFullBleed
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockFullBleedImage
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              blockData={blockItems}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.FULL_BLEED_CARD_LEFT_CENTERED:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isFullBleed
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCardCentered
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              isFullBleed
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.FULL_BLEED_CARD_RIGHT_CENTERED:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isFullBleed
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockCardCentered
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              isFullBleed
                              editHandler={() => { }}
                              blockData={blockItems}
                              previewMode={previewMode}
                            />
                          </BlockSlot>
                        );
                      case BlockType.LINED_TITLE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockLinedTitle onCloseImageModal={() => { }} showImageModal={false} data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.SNS_OLD:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockSNSOldPreview data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.SNS:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockSNSPreview data={item} isEditing={false} isTemplate={false} editHandler={() => { }} previewMode={previewMode} />
                          </BlockSlot>
                        );
                      case BlockType.ANIMATION_COMPONENT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockAnimationBlock
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              previewMode="smartphone"
                            />
                          </BlockSlot>
                        );
                      case BlockType.ANIMATION_WITH_DESCRIPTION_COMPONENT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockAnimationWithImageDescription
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              previewMode="smartphone"
                            />
                          </BlockSlot>
                        );
                      case BlockType.FOOTER:
                      case BlockType.FOOTER_FREE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            isFooter={!item.data?.isFree}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            handleImageIconClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockFooter
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.CONTACT:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockContact
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                            />
                          </BlockSlot>
                        );
                      case BlockType.VIDEO:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            isStaticInBackground={item.data?.video?.settings?.staticBackground}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockVideo data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_WITH_COLUMNS:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListWithColumns
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_WITH_EQUAL_SPACE:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListWithEqualSpace
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_FIXED_MAX_2:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListFixedMax2
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_FIXED_MAX_3:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListFixedMax3
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_FIXED_MOBILE_2:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListFixedMobile2
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_EQUAL_MOBILE_2:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListEqualMobile2
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_EQUAL_MAX_2:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListEqualMax2
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.BLOCK_MENU_LIST_EQUAL_MAX_3:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleImageIconClicked={() => { }}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockMenuListEqualMax3
                              mainColor={main_color}
                              onCloseImageModal={() => { }}
                              showImageModal={false}
                              data={item}
                              isEditing={false}
                              isTemplate={false}
                              editHandler={() => { }}
                              blockData={blockItems}
                            />
                          </BlockSlot>
                        );
                      case BlockType.I_FRAME_SERVICES:
                        return (
                          <BlockSlot
                            key={item.id}
                            dataId={item.id}
                            isEditing={false}
                            handlePanelEvent={null}
                            handleBlockAddClicked={() => { }}
                            animation={animation}
                            defaultBackgroundColor={background_color ?? '#fff'}
                          >
                            <BlockiFrameServices data={item} isEditing={false} isTemplate={false} editHandler={() => { }} />
                          </BlockSlot>
                        );
                      default:
                        return (
                          <div />
                        );
                    }
                  })}
                </Stack>
              </Box>
            </Grid>
          </Box>
        </Grid>
        <AdContainer />
      </Grid>
    </>
  );
}

export default BlocksContainerExport;
